import * as fabric from "fabric";
import WebFont from 'webfontloader';

export const PLACEHOLDER_TEXT = "Entrez votre texte ici...";
export const MINFONTSIZE = 12;
export const MAXFONTSIZE = 60;


/**
 * Calculates the width of a given text string based on the specified font size and font family.
 *
 * The function creates a temporary canvas element and determines the text width
 * by using the 2D rendering context of the canvas.
 *
 * @param {string} text - The text string whose width needs to be measured.
 * @param {number} fontSize - The font size to be applied when measuring the text.
 * @param {string} fontFamily - The font family to be applied when measuring the text.
 * @returns {number} The measured width of the text in pixels.
 */
const calculateTextWidth = (text: string, fontSize: number, fontFamily: string): number => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  context!.font = `${fontSize}px ${fontFamily}`;
  return context!.measureText(text).width;
};

/**
 * A function that determines if the width of a given text inside a textbox exceeds the specified maximum width.
 *
 * @param {Object} textbox - The textbox object containing the text, font size, and font family.
 * @param {string} textbox.text - The text to measure the width of.
 * @param {string} textbox.fontSize - The font size of the text.
 * @param {string} textbox.fontFamily - The font family of the text.
 * @param {Object} maxSizes - An object containing the maximum allowable dimensions.
 * @param {number} maxSizes.width - The maximum allowable width for the text.
 * @returns {boolean} - Returns true if the text's width exceeds the specified maximum width, otherwise false.
 */
const isOverWidth = ( textbox: { text: string; fontSize: string; fontFamily: string; }, maxSizes: { width: number; } ): boolean => calculateTextWidth( textbox.text,
  Number(textbox.fontSize),
  textbox.fontFamily ) > maxSizes.width;
const isOverHeight = ( textbox, maxSizes ) => textbox.getScaledHeight() > maxSizes.height;
export const isOverSize = ( textbox, maxSizes ) => isOverWidth( textbox, maxSizes ) || isOverHeight( textbox, maxSizes );


/**
 * Resets the styles of a given Fabric.js Textbox instance by removing
 * the `fontSize` property from all styled characters within the textbox.
 *
 * This method iterates through all the style objects present in the textbox,
 * targeting each line and character, and deletes the `fontSize` property
 * where it exists. After completion, the textbox's `dirty` flag is set
 * to `true`, marking it for re-rendering.
 *
 * @param {fabric.Textbox} textbox - The Fabric.js Textbox instance to reset styles for.
 * @returns {void}
 */
const resetTextboxStyles = (textbox: fabric.Textbox): void => {
  if (textbox.styles) {
    Object.keys(textbox.styles).forEach(lineIndex => {
      Object.keys(textbox.styles[lineIndex]).forEach(charIndex => {
        delete textbox.styles[lineIndex][charIndex].fontSize;
      });
    });
  }
  textbox.dirty = true;
};

/**
 * Determines if a textbox contains any word that exceeds the specified maximum width.
 *
 * @param {Object} textbox - The textbox object containing text, font size, and font family.
 * @param {Object} maxSizes - The maximum allowed dimensions for the textbox content.
 * @param {number} maxSizes.width - The maximum width a word can occupy in the textbox.
 * @returns {boolean} - Returns `true` if any word in the textbox exceeds the specified maximum width, otherwise
 *   `false`.
 */
const hasTooLongWord = (textbox, maxSizes) => {
  const words = textbox.text.split( /\s+/ );
  return words.some( ( word ) => {
    const wordWidth = calculateTextWidth( word, textbox.fontSize, textbox.fontFamily );
    return wordWidth > maxSizes.width;
  } );
};

/**
 * Adjusts the font size of a textbox to ensure it fits within the specified maximum dimensions.
 *
 * The function reduces the font size of the given textbox incrementally to ensure
 * it does not exceed the specified width and height constraints. It stops adjusting
 * if the font size reaches the minimum allowed value or if the textbox meets the
 * required size constraints.
 *
 * @param {Object} textbox - The textbox object whose font size needs adjustment.
 * @param {Object} maxSizes - An object specifying the maximum allowable dimensions.
 * @param {number} maxSizes.width - The maximum width the textbox should occupy.
 * @param {number} maxSizes.height - The maximum height the textbox should occupy.
 */
export const decreaseFontSize = (textbox, maxSizes, scale) => {
  if (!textbox || typeof textbox?.fontSize !== 'number') {
    console.warn("Textbox est invalide ou `fontSize` n'est pas défini.");
    return false;
  }
  let resized = false;
    while ( textbox.fontSize > (MINFONTSIZE * scale) ) {
      if ( isOverSize( textbox, maxSizes ) && maxSizes.width > 0 ) {
        if ( hasTooLongWord( textbox, maxSizes ) || isOverHeight( textbox, maxSizes ) ) {
          textbox.set( { fontSize: textbox.fontSize - 1 } );
          resized = true;
          resetTextboxStyles( textbox );
          textbox.setCoords();
        } else {
          break;
        }
      } else {
        break;
      }
    }
    return resized;

};

/**
 * Splits a given text into an array of wrapped lines based on the specified font settings and maximum line width.
 *
 * @param {string} text - The text to be wrapped.
 * @param {number} fontSize - The font size used for measuring text width.
 * @param {string} fontFamily - The font family used for measuring text width.
 * @param {number} maxWidth - The maximum width allowed for each line.
 * @returns {string[]} An array of strings where each element represents a line of wrapped text.
 */
const getWrappedLines = ( text: string, fontSize: number, fontFamily: string, maxWidth: number ): string[] => {
  const words = text.split( /\s+/ ); // Divise en mots
  let lines: string[] = [];
  let currentLine = "";

  words.forEach( ( word ) => {
    const testLine = currentLine + (currentLine ? " " : "") + word;
    const lineWidth = calculateTextWidth( testLine, fontSize, fontFamily );

    if ( lineWidth > maxWidth && currentLine !== "" ) {
      lines.push( currentLine );
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  } );

  if ( currentLine ) {
    lines.push( currentLine );
  }

  return lines;
};

/**
 * Asynchronously increases the font size of a given textbox element until the maximum font size or
 * constraints such as width or height are reached.
 *
 * @param {Object} textbox - The textbox object whose font size is to be adjusted.
 * @param {Object} maxSizes - Object defining the maximum size constraints.
 * @param {number} maxSizes.fontSize - The maximum allowable font size for the textbox. Defaults to a constant value if not provided.
 * @param {number} maxSizes.width - The maximum allowable width of the textbox.
 * @param {number} maxSizes.height - The maximum allowable height of the textbox.
 *
 * @throws {Error} If any required properties are missing or invalid, depending on helper functions.
 *
 * @function
 */
export const increaseFontSize = async (textbox, maxSizes, scale) => {
  if (!textbox || typeof textbox?.fontSize !== 'number') {
    console.warn("Textbox est invalide ou `fontSize` n'est pas défini.");
    return false;
  }

  const maxFontSize = maxSizes.fontSize || (MAXFONTSIZE * scale);
  
  while ( textbox.fontSize < maxFontSize ) {
    textbox.set( { fontSize: textbox.fontSize + 1 } );

    const wrappedLines = getWrappedLines(
      textbox.text,
      textbox.fontSize,
      textbox.fontFamily,
      maxSizes.width
    );

    const anyLineOverWidth = wrappedLines.some( ( line ) => {
      const lineWidth = calculateTextWidth( line, textbox.fontSize, textbox.fontFamily );
      return lineWidth > maxSizes.width;
    } );

    if ( anyLineOverWidth || isOverHeight( textbox, maxSizes ) ) {
      textbox.set( { fontSize: textbox.fontSize - 1 } );
      maxSizes.width > 0 ? textbox.set( { width: maxSizes.width } ) : null;
      break;
    }
  }
};


/**
 * Creates a Fabric.js Textbox instance with predefined locked settings and additional options.
 *
 * @function createFabricTextbox
 * @param {string} text - The text content to display in the Fabric.js Textbox.
 * @param {Object} options - Additional configuration options to customize the appearance and behavior of the Textbox.
 * @returns {fabric.Textbox} A new Fabric.js Textbox instance with the specified text and options applied.
 */
export const createFabricTextbox = (
  text,
  options
): fabric.Textbox => {
  return new fabric.Textbox(text, {
    lockScalingX: true,
    lockScalingY: true,
    lockMovementX: true,
    lockMovementY: true,
    lockRotation: true,
    hasControls: false,
    hasBorders: true,
    hoverCursor: "text",
    editingCursor: "text",
    placeholder: true,
    ...options,
  });
};

/**
 * Calculates the new scale factor for resizing an element based on its text style width,
 * the width of the fabric element containing it, and the current scale factor.
 *
 * @param {string} itemTextStyleWidth - The width of the item's text style as a string, which will be parsed into a float.
 * @param {number} fabricElementWidth - The width of the fabric element that contains the item.
 * @param {number} scale - The current scaling factor applied to the item.
 * @returns {number} The new scale factor computed for the item based on the input values.
 */
export const calculateNewScale = (
  itemTextStyleWidth: string,
  fabricElementWidth: number,
  scale: number
): number => (parseFloat(itemTextStyleWidth) * scale) / fabricElementWidth;